
// src/pages/achats/DetailFournisseur.jsx
import React, { useState, useEffect } from "react";
import {
    Card,
    Row,
    Col,
    Button,
    Badge,
    Table,
    Spinner,
    Modal,
    Tooltip,
    OverlayTrigger
} from "react-bootstrap";
import { useParams, useNavigate, Link } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION
   ============================================================ */
const ToastNotification = ({
    show,
    onClose,
    type,
    title,
    message,
    autoHideDelay = 5000
}) => {
    if (!show) return null;

    const config = {
        success: {
            bgColor: COLORS.success || "#10b981",
            icon: "ri-checkbox-circle-fill"
        },
        error: {
            bgColor: COLORS.danger || "#ef4444",
            icon: "ri-error-warning-fill"
        },
        info: {
            bgColor: COLORS.info || "#3b82f6",
            icon: "ri-information-fill"
        },
        warning: {
            bgColor: COLORS.warning || "#f59e0b",
            icon: "ri-alert-fill"
        }
    }[type] || {
        bgColor: COLORS.info || "#3b82f6",
        icon: "ri-information-fill"
    };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div
                    className="toast-header-custom"
                    style={{ background: config.bgColor }}
                >
                    <div className="d-flex align-items-center gap-2">
                        <i
                            className={`${config.icon} text-white`}
                            style={{ fontSize: "1.4rem" }}
                        />
                        <strong className="text-white fs-6">
                            {title}
                        </strong>
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        className="toast-close-btn"
                    >
                        <i className="ri-close-line fs-5" />
                    </button>
                </div>

                <div className="toast-body-custom">
                    <p className="mb-0">{message}</p>
                </div>

                <div
                    className="toast-progress"
                    style={{
                        background: config.bgColor,
                        animation: `shrink ${autoHideDelay}ms linear forwards`
                    }}
                />
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function DetailFournisseur() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [fournisseur, setFournisseur] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const [toast, setToast] = useState({
        show: false,
        type: "",
        title: "",
        message: ""
    });

    const showToast = (type, title, message) => {
        setToast({
            show: true,
            type,
            title,
            message
        });

        setTimeout(() => {
            setToast({
                show: false,
                type: "",
                title: "",
                message: ""
            });
        }, 5000);
    };

    const hideToast = () => {
        setToast({
            show: false,
            type: "",
            title: "",
            message: ""
        });
    };

    /* ============================================================
       FETCH FOURNISSEUR
       ============================================================ */
    useEffect(() => {
        fetchFournisseur();
    }, [id]);

    const fetchFournisseur = async () => {
        try {
            setLoading(true);
            setError(null);

            const token = localStorage.getItem("token");

            const response = await fetch(
                `${API_BASE_URL}/fournisseurs.php?id=${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem("token");
                    localStorage.removeItem("user");
                    navigate("/pages/signin2");
                    throw new Error("Session expirée");
                }

                throw new Error(`Erreur HTTP: ${response.status}`);
            }

            const result = await response.json();

            if (!result.success && !result.data) {
                throw new Error(
                    result.message || "Impossible de récupérer le fournisseur"
                );
            }

            setFournisseur(result.data);
        } catch (err) {
            console.error("Erreur chargement fournisseur :", err);
            setError(err.message || "Erreur lors du chargement");
        } finally {
            setLoading(false);
        }
    };

    /* ============================================================
       SUPPRESSION
       ============================================================ */
    const handleDelete = async () => {
        if (!fournisseur?.id_fournisseur) return;

        setIsDeleting(true);

        try {
            const token = localStorage.getItem("token");

            const response = await fetch(
                `${API_BASE_URL}/fournisseurs.php?id=${fournisseur.id_fournisseur}`,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status}`);
            }

            const result = await response.json();

            if (result.success) {
                setShowDeleteModal(false);

                showToast(
                    "success",
                    "Suppression réussie",
                    "Le fournisseur a été supprimé avec succès."
                );

                setTimeout(() => {
                    navigate("/achats/fournisseurs");
                }, 900);
            } else {
                showToast(
                    "error",
                    "Erreur",
                    result.message || "Erreur lors de la suppression."
                );

                setShowDeleteModal(false);
            }
        } catch (err) {
            console.error("Erreur suppression fournisseur :", err);

            showToast(
                "error",
                "Erreur",
                "Erreur de connexion au serveur."
            );

            setShowDeleteModal(false);
        } finally {
            setIsDeleting(false);
        }
    };

    /* ============================================================
       HELPERS
       ============================================================ */
    const getNoteStars = (note) => {
        const noteValue = note
            ? Math.min(5, Math.max(0, Math.round(parseFloat(note))))
            : 0;

        return Array.from({ length: 5 }, (_, index) => (
            <i
                key={index}
                className={`ri-star-${
                    index + 1 <= noteValue ? "fill" : "line"
                }`}
                style={{
                    color: COLORS.warning || "#f59e0b",
                    fontSize: "1rem"
                }}
            />
        ));
    };

    const getStatutConfig = (statut) => {
        const configs = {
            recue_complete: {
                label: "Reçue complète",
                background: `${COLORS.success || "#10b981"}15`,
                color: COLORS.success || "#10b981",
                icon: "ri-checkbox-circle-line"
            },
            recue_partielle: {
                label: "Reçue partielle",
                background: `${COLORS.warning || "#f59e0b"}15`,
                color: COLORS.warning || "#f59e0b",
                icon: "ri-time-line"
            },
            brouillon: {
                label: "Brouillon",
                background: "#f1f5f9",
                color: "#64748b",
                icon: "ri-draft-line"
            },
            envoyee: {
                label: "Envoyée",
                background: `${COLORS.info || "#3b82f6"}15`,
                color: COLORS.info || "#3b82f6",
                icon: "ri-send-plane-line"
            },
            annulee: {
                label: "Annulée",
                background: `${COLORS.danger || "#ef4444"}15`,
                color: COLORS.danger || "#ef4444",
                icon: "ri-close-circle-line"
            }
        };

        return (
            configs[statut] || {
                label: statut || "Inconnu",
                background: "#f1f5f9",
                color: "#64748b",
                icon: "ri-information-line"
            }
        );
    };

    const renderStatut = (statut) => {
        const config = getStatutConfig(statut);

        return (
            <span
                className="status-badge-charte"
                style={{
                    background: config.background,
                    color: config.color
                }}
            >
                <i className={config.icon} />
                {config.label}
            </span>
        );
    };

    const getNombreJours = (date) => {
        if (!date) return null;

        const dateCommande = new Date(date);

        if (Number.isNaN(dateCommande.getTime())) {
            return null;
        }

        const difference =
            new Date().getTime() - dateCommande.getTime();

        return Math.max(
            0,
            Math.round(difference / (1000 * 60 * 60 * 24))
        );
    };

    /* ============================================================
       LOADING
       ============================================================ */
    if (loading) {
        return (
            <>
                <Header />

                <div
                    className="main main-app p-3 p-lg-4"
                    style={{
                        background: "#f8fafc",
                        minHeight: "calc(100vh - 100px)"
                    }}
                >
                    <div className="d-flex justify-content-center align-items-center loading-page">
                        <div className="text-center">
                            <div className="loading-spinner-charte">
                                <div className="loading-ring-charte" />
                                <i className="ri-building-2-line loading-icon-charte" />
                            </div>

                            <h5
                                className="fw-bold mt-4 mb-2"
                                style={{
                                    color:
                                        COLORS.primary ||
                                        "#295AA7"
                                }}
                            >
                                Chargement du fournisseur
                            </h5>

                            <p className="text-muted small mb-0">
                                Récupération des informations en cours...
                            </p>
                        </div>
                    </div>
                </div>

                <Footer />

                <style>{getStyles()}</style>
            </>
        );
    }

    /* ============================================================
       ERROR
       ============================================================ */
    if (error || !fournisseur) {
        return (
            <>
                <Header />

                <div
                    className="main main-app p-3 p-lg-4"
                    style={{
                        background: "#f8fafc",
                        minHeight: "calc(100vh - 100px)"
                    }}
                >
                    <div className="container-fluid">
                        <div className="error-state-charte">
                            <div className="error-state-icon">
                                <i className="ri-error-warning-line" />
                            </div>

                            <h5 className="fw-bold mb-2">
                                Fournisseur non trouvé
                            </h5>

                            <p className="text-muted mb-4">
                                {error ||
                                    "Le fournisseur demandé n'existe pas ou n'est plus disponible."}
                            </p>

                            <Button
                                className="btn-modern-primary"
                                onClick={() =>
                                    navigate("/achats/fournisseurs")
                                }
                            >
                                <i className="ri-arrow-left-line" />
                                Retour aux fournisseurs
                            </Button>
                        </div>
                    </div>
                </div>

                <Footer />

                <style>{getStyles()}</style>
            </>
        );
    }

    const commandes = Array.isArray(fournisseur.commandes)
        ? fournisseur.commandes
        : [];

    const nbCommandes =
        Number(fournisseur.nb_commandes) || commandes.length || 0;

    const totalAchats =
        Number(fournisseur.total_achats) || 0;

    const noteMoyenne = fournisseur.note_moyenne
        ? parseFloat(fournisseur.note_moyenne)
        : null;

    const joursDepuisDerniereCommande =
        getNombreJours(fournisseur.derniere_commande);

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />

            <div
                className="main main-app p-3 p-lg-4"
                style={{
                    background: "#f8fafc",
                    minHeight: "100vh"
                }}
            >
                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ====================================================
                       EN-TÊTE
                       ==================================================== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-truck-line" />
                            </div>

                            <div>
                                <h5 className="header-title">
                                    Fiche fournisseur
                                </h5>

                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-building-line" />
                                        {fournisseur.raison_sociale}
                                    </span>

                                    <span
                                        className="badge-modern"
                                        style={{
                                            background: `${COLORS.secondary || "#21ACCD"}15`,
                                            color:
                                                COLORS.secondary ||
                                                "#21ACCD"
                                        }}
                                    >
                                        <i className="ri-barcode-line" />
                                        {fournisseur.code_fournisseur ||
                                            "Sans code"}
                                    </span>

                                    <span
                                        className="badge-modern"
                                        style={{
                                            background: `${COLORS.success || "#10b981"}15`,
                                            color:
                                                COLORS.success ||
                                                "#10b981"
                                        }}
                                    >
                                        <i className="ri-shopping-bag-line" />
                                        {nbCommandes} commande
                                        {nbCommandes !== 1 ? "s" : ""}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="d-flex gap-2 flex-wrap">
                            <Button
                                className="btn-modern-outline"
                                onClick={() =>
                                    navigate("/achats/fournisseurs")
                                }
                            >
                                <i className="ri-arrow-left-line" />
                                Retour
                            </Button>

                            <Button
                                className="btn-modern-outline"
                                as={Link}
                                to={`/achats/fournisseurs/modifier/${fournisseur.id_fournisseur}`}
                            >
                                <i className="ri-pencil-line" />
                                Modifier
                            </Button>

                            <Button
                                className="btn-modern-primary"
                                onClick={() =>
                                    navigate(
                                        "/achats/commandesNouveau"
                                    )
                                }
                            >
                                <i className="ri-add-line" />
                                Nouvelle commande
                            </Button>
                        </div>
                    </div>

                    {/* ====================================================
                       PROFIL FOURNISSEUR
                       ==================================================== */}
                    <Card className="profile-card-charte mb-4">
                        <Card.Body className="p-4">
                            <Row className="align-items-center g-4">

                                <Col lg={8}>
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="supplier-avatar-charte">
                                            <i className="ri-truck-line" />
                                        </div>

                                        <div className="supplier-main-info">
                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                                <h4 className="supplier-name mb-0">
                                                    {
                                                        fournisseur.raison_sociale
                                                    }
                                                </h4>

                                                <span className="active-badge-charte">
                                                    <span className="status-dot" />
                                                    Fournisseur
                                                </span>
                                            </div>

                                            <div className="supplier-meta mt-2">
                                                <span>
                                                    <i className="ri-barcode-line me-1" />
                                                    Code :
                                                    <strong className="ms-1">
                                                        {
                                                            fournisseur.code_fournisseur ||
                                                            "-"
                                                        }
                                                    </strong>
                                                </span>

                                                <span className="meta-separator">
                                                    •
                                                </span>

                                                <span>
                                                    <i className="ri-calendar-line me-1" />
                                                    Depuis{" "}
                                                    <strong className="ms-1">
                                                        {formatDate(
                                                            fournisseur.date_creation
                                                        )}
                                                    </strong>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </Col>

                                <Col lg={4}>
                                    <div className="profile-actions">
                                        <Button
                                            className="btn-modern-outline"
                                            as={Link}
                                            to={`/achats/fournisseurs/modifier/${fournisseur.id_fournisseur}`}
                                        >
                                            <i className="ri-pencil-line" />
                                            Modifier la fiche
                                        </Button>
                                    </div>
                                </Col>

                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ====================================================
                       KPI
                       ==================================================== */}
                    <Row className="g-3 mb-4">

                        <Col sm={6} xl={3}>
                            <div className="kpi-charte kpi-primary">
                                <div className="kpi-icon-charte">
                                    <i className="ri-shopping-bag-line" />
                                </div>

                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">
                                        Total achats
                                    </span>

                                    <h3 className="kpi-value-charte">
                                        {formatCurrency(totalAchats)}
                                    </h3>

                                    <span className="kpi-unit-charte">
                                        {nbCommandes} commande
                                        {nbCommandes !== 1
                                            ? "s"
                                            : ""}
                                    </span>
                                </div>
                            </div>
                        </Col>

                        <Col sm={6} xl={3}>
                            <div className="kpi-charte kpi-success">
                                <div className="kpi-icon-charte">
                                    <i className="ri-calendar-check-line" />
                                </div>

                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">
                                        Dernière commande
                                    </span>

                                    <h3
                                        className="kpi-value-charte"
                                        style={{
                                            fontSize: "1.15rem"
                                        }}
                                    >
                                        {fournisseur.derniere_commande
                                            ? formatDate(
                                                  fournisseur.derniere_commande
                                              )
                                            : "Jamais"}
                                    </h3>

                                    <span className="kpi-unit-charte">
                                        {fournisseur.derniere_commande
                                            ? `${joursDepuisDerniereCommande} jour${
                                                  joursDepuisDerniereCommande !==
                                                  1
                                                      ? "s"
                                                      : ""
                                              }`
                                            : "Aucune commande"}
                                    </span>
                                </div>
                            </div>
                        </Col>

                        <Col sm={6} xl={3}>
                            <div className="kpi-charte kpi-warning">
                                <div className="kpi-icon-charte">
                                    <i className="ri-star-line" />
                                </div>

                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">
                                        Note moyenne
                                    </span>

                                    <h3 className="kpi-value-charte">
                                        {noteMoyenne !== null
                                            ? noteMoyenne.toFixed(1)
                                            : "N/A"}
                                    </h3>

                                    <div className="kpi-stars">
                                        {getNoteStars(noteMoyenne)}
                                    </div>
                                </div>
                            </div>
                        </Col>

                        <Col sm={6} xl={3}>
                            <div className="kpi-charte kpi-info">
                                <div className="kpi-icon-charte">
                                    <i className="ri-file-list-3-line" />
                                </div>

                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">
                                        Code fournisseur
                                    </span>

                                    <h3
                                        className="kpi-value-charte"
                                        style={{
                                            fontSize: "1.1rem"
                                        }}
                                    >
                                        {fournisseur.code_fournisseur ||
                                            "-"}
                                    </h3>

                                    <span className="kpi-unit-charte">
                                        Identifiant fournisseur
                                    </span>
                                </div>
                            </div>
                        </Col>

                    </Row>

                    {/* ====================================================
                       INFORMATIONS
                       ==================================================== */}
                    <Row className="g-3 mb-4">

                        {/* COORDONNÉES */}
                        <Col lg={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="card-header-gradient">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-contacts-line text-white fs-5" />
                                        <span className="fw-semibold text-white">
                                            Coordonnées
                                        </span>
                                    </div>
                                </Card.Header>

                                <Card.Body className="p-0">
                                    <div className="info-list-charte">

                                        <div className="info-item-charte">
                                            <div className="info-icon-charte info-icon-primary">
                                                <i className="ri-phone-line" />
                                            </div>

                                            <div className="info-text-charte">
                                                <span className="info-label-charte">
                                                    Téléphone
                                                </span>
                                                <strong className="info-value-charte">
                                                    {fournisseur.telephone ||
                                                        "Non renseigné"}
                                                </strong>
                                            </div>
                                        </div>

                                        <div className="info-item-charte">
                                            <div className="info-icon-charte info-icon-info">
                                                <i className="ri-mail-line" />
                                            </div>

                                            <div className="info-text-charte">
                                                <span className="info-label-charte">
                                                    Adresse e-mail
                                                </span>
                                                <strong className="info-value-charte">
                                                    {fournisseur.email ||
                                                        "Non renseigné"}
                                                </strong>
                                            </div>
                                        </div>

                                        <div className="info-item-charte">
                                            <div className="info-icon-charte info-icon-success">
                                                <i className="ri-map-pin-line" />
                                            </div>

                                            <div className="info-text-charte">
                                                <span className="info-label-charte">
                                                    Adresse
                                                </span>
                                                <strong className="info-value-charte">
                                                    {fournisseur.adresse ||
                                                        "Non renseignée"}
                                                </strong>
                                            </div>
                                        </div>

                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* IDENTIFIANTS JURIDIQUES */}
                        <Col lg={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="card-header-gradient">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="ri-briefcase-4-line text-white fs-5" />
                                        <span className="fw-semibold text-white">
                                            Identifiants juridiques
                                        </span>
                                    </div>
                                </Card.Header>

                                <Card.Body className="p-0">
                                    <div className="legal-grid">

                                        <div className="legal-item-charte">
                                            <span className="legal-label">
                                                ICE
                                            </span>
                                            <strong>
                                                {fournisseur.ice || "-"}
                                            </strong>
                                        </div>

                                        <div className="legal-item-charte">
                                            <span className="legal-label">
                                                Patente
                                            </span>
                                            <strong>
                                                {fournisseur.patente ||
                                                    "-"}
                                            </strong>
                                        </div>

                                        <div className="legal-item-charte">
                                            <span className="legal-label">
                                                RC
                                            </span>
                                            <strong>
                                                {fournisseur.rc || "-"}
                                            </strong>
                                        </div>

                                        <div className="legal-item-charte">
                                            <span className="legal-label">
                                                IF
                                            </span>
                                            <strong>
                                                {fournisseur.identifiant_fiscal ||
                                                    "-"}
                                            </strong>
                                        </div>

                                        <div className="legal-item-charte">
                                            <span className="legal-label">
                                                CNSS
                                            </span>
                                            <strong>
                                                {fournisseur.cnss || "-"}
                                            </strong>
                                        </div>

                                    </div>

                                    {fournisseur.conditions_reglement && (
                                        <div className="condition-box-charte">
                                            <div className="condition-title">
                                                <i className="ri-file-text-line" />
                                                Conditions de règlement
                                            </div>

                                            <p className="mb-0">
                                                {
                                                    fournisseur.conditions_reglement
                                                }
                                            </p>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                    </Row>

                    {/* ====================================================
                       COMMANDES
                       ==================================================== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">

                        <Card.Header className="card-header-gradient">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">

                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-history-line text-white fs-5" />

                                    <span className="fw-semibold text-white">
                                        Historique des commandes
                                    </span>

                                    <span className="badge-header-light">
                                        {commandes.length}
                                    </span>
                                </div>

                                <Button
                                    className="btn-header-light"
                                    onClick={() =>
                                        navigate("/achats/commandes")
                                    }
                                >
                                    <i className="ri-eye-line" />
                                    Voir toutes les commandes
                                </Button>

                            </div>
                        </Card.Header>

                        <Card.Body className="p-0">

                            {commandes.length > 0 ? (
                                <div className="table-responsive">
                                    <Table
                                        hover
                                        className="align-middle mb-0 table-charte"
                                    >
                                        <thead>
                                            <tr>
                                                <th className="ps-4">
                                                    N° Commande
                                                </th>

                                                <th>
                                                    Date
                                                </th>

                                                <th className="text-end">
                                                    Montant
                                                </th>

                                                <th>
                                                    Statut
                                                </th>

                                                <th className="text-center">
                                                    Détails
                                                </th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {commandes.map((commande) => (
                                                <tr
                                                    key={
                                                        commande.id_commande_fournisseur
                                                    }
                                                >
                                                    <td className="ps-4">
                                                        <Link
                                                            to={`/achats/commandes/${commande.id_commande_fournisseur}`}
                                                            className="reference-badge-charte reference-link"
                                                        >
                                                            <i className="ri-file-list-3-line" />
                                                            {
                                                                commande.numero_commande
                                                            }
                                                        </Link>
                                                    </td>

                                                    <td>
                                                        <div className="date-cell-charte">
                                                            <span>
                                                                {formatDate(
                                                                    commande.date_commande
                                                                )}
                                                            </span>

                                                            {commande.date_commande && (
                                                                <small>
                                                                    <i className="ri-calendar-line me-1" />
                                                                    Commande
                                                                </small>
                                                            )}
                                                        </div>
                                                    </td>

                                                    <td className="text-end">
                                                        <span className="amount-charte">
                                                            {formatCurrency(
                                                                commande.montant_ttc ||
                                                                    0
                                                            )}
                                                        </span>
                                                    </td>

                                                    <td>
                                                        {renderStatut(
                                                            commande.statut
                                                        )}
                                                    </td>

                                                    <td className="text-center">
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={
                                                                <Tooltip>
                                                                    Voir les
                                                                    détails
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <button
                                                                type="button"
                                                                className="action-btn-charte"
                                                                style={{
                                                                    "--btn-color":
                                                                        COLORS.primary ||
                                                                        "#295AA7"
                                                                }}
                                                                onClick={() =>
                                                                    navigate(
                                                                        `/achats/commandes/${commande.id_commande_fournisseur}`
                                                                    )
                                                                }
                                                            >
                                                                <i className="ri-eye-line" />
                                                            </button>
                                                        </OverlayTrigger>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="empty-state-charte">
                                    <div className="empty-state-icon-charte">
                                        <i className="ri-file-list-3-line" />
                                    </div>

                                    <h6 className="fw-semibold mb-1">
                                        Aucune commande
                                    </h6>

                                    <p className="text-muted small mb-3">
                                        Ce fournisseur ne possède encore
                                        aucune commande.
                                    </p>

                                    <Button
                                        className="btn-modern-primary"
                                        onClick={() =>
                                            navigate(
                                                "/achats/commandesNouveau"
                                            )
                                        }
                                    >
                                        <i className="ri-add-line" />
                                        Créer une commande
                                    </Button>
                                </div>
                            )}

                        </Card.Body>
                    </Card>

                    {/* ====================================================
                       MÉTADONNÉES
                       ==================================================== */}
                    <div className="footer-info-card mt-4">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">

                            <div className="footer-meta">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="footer-meta-icon">
                                        <i className="ri-information-line" />
                                    </div>

                                    <div>
                                        <div className="footer-meta-title">
                                            Informations système
                                        </div>

                                        <div className="footer-meta-text">
                                            ID #{fournisseur.id_fournisseur}
                                            <span className="mx-2">•</span>
                                            Créé le{" "}
                                            {formatDate(
                                                fournisseur.date_creation
                                            )}

                                            {fournisseur.date_modification && (
                                                <>
                                                    <span className="mx-2">
                                                        •
                                                    </span>
                                                    Modifié le{" "}
                                                    {formatDate(
                                                        fournisseur.date_modification
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <Button
                                className="btn-modern-danger-outline"
                                onClick={() =>
                                    setShowDeleteModal(true)
                                }
                            >
                                <i className="ri-delete-bin-line" />
                                Supprimer le fournisseur
                            </Button>

                        </div>
                    </div>

                </div>

                <Footer />
            </div>

            {/* ============================================================
               MODAL SUPPRESSION
               ============================================================ */}
            <Modal
                show={showDeleteModal}
                onHide={() =>
                    !isDeleting && setShowDeleteModal(false)
                }
                centered
                className="modal-charte"
            >
                <Modal.Header
                    closeButton
                    className="modal-header-charte"
                    style={{
                        background: `linear-gradient(
                            135deg,
                            ${COLORS.danger || "#ef4444"},
                            #dc2626
                        )`
                    }}
                >
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon">
                            <i className="ri-delete-bin-line" />
                        </div>

                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">
                                Confirmer la suppression
                            </Modal.Title>

                            <p className="text-white-50 mb-0 small">
                                Cette action est irréversible
                            </p>
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Body className="p-4">

                    <p className="text-center text-muted mb-4">
                        Êtes-vous sûr de vouloir supprimer définitivement
                        ce fournisseur ?
                    </p>

                    {/* Résumé fournisseur */}
                    <div className="summary-card-charte mb-3">
                        <div className="d-flex align-items-center gap-3">

                            <div className="supplier-avatar-modal">
                                <i className="ri-truck-line" />
                            </div>

                            <div>
                                <div
                                    className="fw-bold"
                                    style={{
                                        color:
                                            COLORS.primaryDark ||
                                            "#1e3f7a"
                                    }}
                                >
                                    {fournisseur.raison_sociale}
                                </div>

                                <div className="text-muted small">
                                    <i className="ri-barcode-line me-1" />
                                    Code :{" "}
                                    {fournisseur.code_fournisseur ||
                                        "-"}
                                </div>

                                <div className="text-muted small">
                                    <i className="ri-shopping-bag-line me-1" />
                                    Commandes : {nbCommandes}
                                </div>

                                <div className="text-muted small">
                                    <i className="ri-money-euro-circle-line me-1" />
                                    Total achats :{" "}
                                    {formatCurrency(totalAchats)}
                                </div>
                            </div>

                        </div>
                    </div>

                    {/* Alerte */}
                    <div className="alert-error-charte mb-0">
                        <div className="d-flex align-items-center">

                            <div className="alert-error-icon">
                                <i className="ri-alert-line" />
                            </div>

                            <div className="ms-3">
                                <strong>Attention</strong>

                                <p className="mb-0 small">
                                    La suppression du fournisseur entraînera
                                    la perte définitive de ses informations.
                                    Les commandes existantes ne seront pas
                                    affectées.
                                </p>
                            </div>

                        </div>
                    </div>

                </Modal.Body>

                <Modal.Footer className="border-0 pb-4 px-4 pt-0">

                    <Button
                        onClick={() =>
                            setShowDeleteModal(false)
                        }
                        className="btn-modern-outline"
                        disabled={isDeleting}
                    >
                        Annuler
                    </Button>

                    <Button
                        onClick={handleDelete}
                        disabled={isDeleting}
                        className="btn-modern-danger"
                    >
                        {isDeleting ? (
                            <>
                                <Spinner
                                    size="sm"
                                    className="me-2"
                                />
                                Suppression...
                            </>
                        ) : (
                            <>
                                <i className="ri-delete-bin-line me-2" />
                                Oui, supprimer
                            </>
                        )}
                    </Button>

                </Modal.Footer>
            </Modal>

            <style>{getStyles()}</style>
        </>
    );
}

/* ============================================================
   STYLES — MÊME CHARTE QUE OperationsCaisse.jsx
   ============================================================ */
function getStyles() {
    return `
        :root {
            --primary: ${COLORS.primary || "#295AA7"};
            --primary-dark: ${COLORS.primaryDark || "#1e3f7a"};
            --secondary: ${COLORS.secondary || "#21ACCD"};
            --success: ${COLORS.success || "#10b981"};
            --danger: ${COLORS.danger || "#ef4444"};
            --warning: ${COLORS.warning || "#f59e0b"};
            --info: ${COLORS.info || "#3b82f6"};
        }

        /* ========================================================
           HEADER
           ======================================================== */

        .header-modern {
            background: white;
            padding: 1.2rem 1.8rem;
            box-shadow: 0 2px 8px rgba(0,0,0,0.04);
            border-left: 4px solid var(--secondary);
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 1rem;
        }

        .header-left {
            display: flex;
            align-items: center;
            gap: 1rem;
            min-width: 0;
        }

        .header-icon-wrapper {
            width: 52px;
            height: 52px;
            background: linear-gradient(
                135deg,
                var(--primary),
                var(--secondary)
            );
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 14px;
            color: white;
            font-size: 1.5rem;
            flex-shrink: 0;
            box-shadow: 0 4px 12px rgba(41,90,167,0.25);
        }

        .header-title {
            color: #1e293b;
            font-size: 1.3rem;
            font-weight: 700;
            margin: 0;
        }

        .badge-modern {
            background: #f1f4f9;
            color: #475569;
            padding: 0.3rem 0.8rem;
            border-radius: 50px;
            font-size: 0.75rem;
            font-weight: 600;
            display: inline-flex;
            align-items: center;
            gap: 6px;
            max-width: 100%;
        }

        .badge-modern i {
            color: var(--secondary);
        }

        /* ========================================================
           PROFILE
           ======================================================== */

        .profile-card-charte {
            border: none;
            border-radius: 12px;
            background: white;
            box-shadow: 0 2px 8px rgba(0,0,0,0.04);
            overflow: hidden;
            position: relative;
        }

        .profile-card-charte::before {
            content: "";
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 4px;
            background: linear-gradient(
                180deg,
                var(--primary),
                var(--secondary)
            );
        }

        .supplier-avatar-charte {
            width: 76px;
            height: 76px;
            border-radius: 18px;
            background: linear-gradient(
                135deg,
                var(--primary),
                var(--primary-dark)
            );
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 2rem;
            flex-shrink: 0;
            box-shadow: 0 8px 20px rgba(41,90,167,0.22);
        }

        .supplier-name {
            color: #1e293b;
            font-size: 1.35rem;
            font-weight: 700;
        }

        .active-badge-charte {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 0.3rem 0.7rem;
            border-radius: 50px;
            background: ${COLORS.success || "#10b981"}15;
            color: ${COLORS.success || "#10b981"};
            font-size: 0.72rem;
            font-weight: 600;
        }

        .status-dot {
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: var(--success);
        }

        .supplier-meta {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 8px;
            color: #64748b;
            font-size: 0.8rem;
        }

        .supplier-meta i {
            color: var(--secondary);
        }

        .meta-separator {
            color: #cbd5e1;
        }

        .profile-actions {
            display: flex;
            justify-content: flex-end;
        }

        /* ========================================================
           KPI
           ======================================================== */

        .kpi-charte {
            background: white;
            border-radius: 12px;
            padding: 1.2rem;
            display: flex;
            align-items: center;
            gap: 1rem;
            box-shadow: 0 2px 8px rgba(0,0,0,0.04);
            border-left: 4px solid;
            transition: all 0.25s ease;
            height: 100%;
        }

        .kpi-charte:hover {
            transform: translateY(-3px);
            box-shadow: 0 12px 28px rgba(0,0,0,0.08);
        }

        .kpi-primary {
            border-left-color: var(--primary);
        }

        .kpi-success {
            border-left-color: var(--success);
        }

        .kpi-warning {
            border-left-color: var(--warning);
        }

        .kpi-info {
            border-left-color: var(--info);
        }

        .kpi-icon-charte {
            width: 48px;
            height: 48px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.4rem;
            flex-shrink: 0;
        }

        .kpi-primary .kpi-icon-charte {
            background: ${COLORS.primary || "#295AA7"}15;
            color: var(--primary);
        }

        .kpi-success .kpi-icon-charte {
            background: ${COLORS.success || "#10b981"}15;
            color: var(--success);
        }

        .kpi-warning .kpi-icon-charte {
            background: ${COLORS.warning || "#f59e0b"}15;
            color: var(--warning);
        }

        .kpi-info .kpi-icon-charte {
            background: ${COLORS.info || "#3b82f6"}15;
            color: var(--info);
        }

        .kpi-content-charte {
            flex: 1;
            min-width: 0;
        }

        .kpi-label-charte {
            font-size: 0.7rem;
            color: #94a3b8;
            text-transform: uppercase;
            font-weight: 600;
            letter-spacing: 0.5px;
        }

        .kpi-value-charte {
            font-size: 1.3rem;
            font-weight: 700;
            margin: 0.3rem 0;
            color: #1e293b;
        }

        .kpi-unit-charte {
            font-size: 0.7rem;
            color: #94a3b8;
        }

        .kpi-stars {
            display: flex;
            align-items: center;
            gap: 2px;
            margin-top: 3px;
        }

        /* ========================================================
           CARD HEADER
           ======================================================== */

        .card-header-gradient {
            background: linear-gradient(
                135deg,
                var(--primary) 0%,
                var(--primary-dark) 100%
            );
            padding: 1rem 1.5rem;
            border: none;
        }

        .badge-header-light {
            background: rgba(255,255,255,0.2);
            color: white;
            border: 1px solid rgba(255,255,255,0.3);
            border-radius: 50px;
            padding: 0.25rem 0.75rem;
            font-size: 0.75rem;
            font-weight: 600;
        }

        .btn-header-light {
            background: rgba(255,255,255,0.2);
            border: 1px solid rgba(255,255,255,0.3);
            color: white;
            border-radius: 50px;
            padding: 0.35rem 0.9rem;
            font-size: 0.75rem;
            font-weight: 500;
            transition: all 0.2s ease;
            display: inline-flex;
            align-items: center;
            gap: 4px;
        }

        .btn-header-light:hover,
        .btn-header-light:focus {
            background: rgba(255,255,255,0.3);
            color: white;
            border-color: rgba(255,255,255,0.5);
        }

        /* ========================================================
           INFORMATIONS
           ======================================================== */

        .info-list-charte {
            padding: 0.5rem 0;
        }

        .info-item-charte {
            display: flex;
            align-items: center;
            gap: 1rem;
            padding: 0.95rem 1.5rem;
            border-bottom: 1px solid #f1f5f9;
            transition: background 0.15s ease;
        }

        .info-item-charte:last-child {
            border-bottom: none;
        }

        .info-item-charte:hover {
            background: #f8fafc;
        }

        .info-icon-charte {
            width: 40px;
            height: 40px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.1rem;
            flex-shrink: 0;
        }

        .info-icon-primary {
            background: ${COLORS.primary || "#295AA7"}15;
            color: var(--primary);
        }

        .info-icon-info {
            background: ${COLORS.info || "#3b82f6"}15;
            color: var(--info);
        }

        .info-icon-success {
            background: ${COLORS.success || "#10b981"}15;
            color: var(--success);
        }

        .info-text-charte {
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 3px;
        }

        .info-label-charte {
            color: #94a3b8;
            font-size: 0.7rem;
            text-transform: uppercase;
            font-weight: 600;
            letter-spacing: 0.4px;
        }

        .info-value-charte {
            color: #1e293b;
            font-size: 0.85rem;
            word-break: break-word;
        }

        /* ========================================================
           JURIDIQUE
           ======================================================== */

        .legal-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            padding: 0.5rem 1.5rem;
        }

        .legal-item-charte {
            padding: 0.85rem 0.5rem;
            border-bottom: 1px solid #f1f5f9;
            display: flex;
            flex-direction: column;
            gap: 4px;
        }

        .legal-item-charte:nth-last-child(-n+2) {
            border-bottom: none;
        }

        .legal-label {
            font-size: 0.68rem;
            text-transform: uppercase;
            letter-spacing: 0.4px;
            color: #94a3b8;
            font-weight: 600;
        }

        .legal-item-charte strong {
            font-size: 0.85rem;
            color: #1e293b;
            word-break: break-word;
        }

        .condition-box-charte {
            margin: 0.5rem 1.5rem 1.25rem;
            padding: 1rem 1.1rem;
            background: #f8fafc;
            border-left: 3px solid var(--secondary);
            border-radius: 10px;
        }

        .condition-title {
            display: flex;
            align-items: center;
            gap: 6px;
            color: var(--primary);
            font-size: 0.8rem;
            font-weight: 700;
            margin-bottom: 0.45rem;
        }

        .condition-box-charte p {
            color: #475569;
            font-size: 0.8rem;
            line-height: 1.5;
        }

        /* ========================================================
           TABLE
           ======================================================== */

        .table-charte thead th {
            background: #f8fafc;
            color: #475569;
            font-weight: 600;
            font-size: 0.7rem;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            border-bottom: 2px solid #e2e8f0;
            padding: 0.9rem 0.75rem;
        }

        .table-charte tbody td {
            padding: 0.9rem 0.75rem;
            vertical-align: middle;
            border-bottom: 1px solid #f1f5f9;
            font-size: 0.85rem;
        }

        .table-charte tbody tr {
            transition: background 0.15s ease;
        }

        .table-charte tbody tr:hover {
            background: #f8fafc;
        }

        .reference-badge-charte {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            background: #f1f5f9;
            padding: 0.3rem 0.65rem;
            border-radius: 6px;
            font-size: 0.72rem;
            font-family: monospace;
            color: #475569;
            transition: all 0.2s ease;
            text-decoration: none;
        }

        .reference-link:hover {
            background: ${COLORS.primary || "#295AA7"}15;
            color: var(--primary);
            text-decoration: none;
        }

        .date-cell-charte {
            display: flex;
            flex-direction: column;
            gap: 2px;
            color: #475569;
        }

        .date-cell-charte small {
            font-size: 0.68rem;
            color: #94a3b8;
        }

        .amount-charte {
            color: var(--primary-dark);
            font-weight: 700;
            white-space: nowrap;
        }

        .status-badge-charte {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 0.3rem 0.75rem;
            border-radius: 50px;
            font-size: 0.72rem;
            font-weight: 700;
            white-space: nowrap;
        }

        /* ========================================================
           ACTION BUTTON
           ======================================================== */

        .action-btn-charte {
            width: 34px;
            height: 34px;
            border-radius: 10px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: white;
            border: 1.5px solid #e2e8f0;
            color: #64748b;
            font-size: 0.95rem;
            transition: all 0.2s ease;
            cursor: pointer;
        }

        .action-btn-charte:hover {
            border-color: var(--btn-color);
            color: var(--btn-color);
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(41,90,167,0.15);
        }

        /* ========================================================
           EMPTY STATE
           ======================================================== */

        .empty-state-charte {
            text-align: center;
            padding: 3rem 2rem;
        }

        .empty-state-icon-charte {
            width: 72px;
            height: 72px;
            background: linear-gradient(
                135deg,
                #f1f5f9,
                #e2e8f0
            );
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 1rem;
            font-size: 1.8rem;
            color: #94a3b8;
        }

        /* ========================================================
           FOOTER INFO
           ======================================================== */

        .footer-info-card {
            background: white;
            border-radius: 12px;
            padding: 1rem 1.25rem;
            box-shadow: 0 2px 8px rgba(0,0,0,0.04);
            border-left: 4px solid #e2e8f0;
        }

        .footer-meta {
            min-width: 0;
        }

        .footer-meta-icon {
            width: 38px;
            height: 38px;
            border-radius: 10px;
            background: #f1f5f9;
            color: #64748b;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
        }

        .footer-meta-title {
            color: #475569;
            font-size: 0.75rem;
            font-weight: 700;
        }

        .footer-meta-text {
            color: #94a3b8;
            font-size: 0.7rem;
            margin-top: 2px;
        }

        /* ========================================================
           BUTTONS
           ======================================================== */

        .btn-modern-outline {
            border: 1.5px solid #d1d5db;
            background: white;
            padding: 0.5rem 1.4rem;
            border-radius: 50px;
            font-size: 0.85rem;
            font-weight: 500;
            color: #4b5563;
            transition: all 0.2s ease;
            display: inline-flex;
            align-items: center;
            gap: 6px;
        }

        .btn-modern-outline:hover,
        .btn-modern-outline:focus {
            border-color: var(--secondary);
            color: var(--secondary);
            background: #f0fbff;
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(33,172,205,0.15);
        }

        .btn-modern-primary {
            background: linear-gradient(
                135deg,
                var(--primary),
                var(--primary-dark)
            );
            border: none;
            color: white;
            padding: 0.5rem 1.6rem;
            border-radius: 50px;
            font-weight: 600;
            font-size: 0.85rem;
            transition: all 0.2s ease;
            box-shadow: 0 4px 12px rgba(41,90,167,0.3);
            display: inline-flex;
            align-items: center;
            gap: 6px;
        }

        .btn-modern-primary:hover,
        .btn-modern-primary:focus {
            transform: translateY(-2px);
            box-shadow: 0 6px 18px rgba(41,90,167,0.4);
            color: white;
        }

        .btn-modern-danger {
            background: linear-gradient(
                135deg,
                var(--danger),
                #dc2626
            );
            border: none;
            color: white;
            padding: 0.5rem 1.6rem;
            border-radius: 50px;
            font-weight: 600;
            font-size: 0.85rem;
            transition: all 0.2s ease;
            box-shadow: 0 4px 12px rgba(239,68,68,0.3);
            display: inline-flex;
            align-items: center;
            gap: 6px;
        }

        .btn-modern-danger:hover,
        .btn-modern-danger:focus {
            transform: translateY(-2px);
            box-shadow: 0 6px 18px rgba(239,68,68,0.4);
            color: white;
        }

        .btn-modern-danger-outline {
            background: white;
            border: 1.5px solid ${COLORS.danger || "#ef4444"};
            color: ${COLORS.danger || "#ef4444"};
            padding: 0.45rem 1rem;
            border-radius: 50px;
            font-size: 0.78rem;
            font-weight: 600;
            display: inline-flex;
            align-items: center;
            gap: 6px;
            transition: all 0.2s ease;
        }

        .btn-modern-danger-outline:hover,
        .btn-modern-danger-outline:focus {
            background: ${COLORS.danger || "#ef4444"};
            border-color: ${COLORS.danger || "#ef4444"};
            color: white;
            transform: translateY(-1px);
        }

        .btn-modern-outline:disabled,
        .btn-modern-primary:disabled,
        .btn-modern-danger:disabled {
            opacity: 0.6;
            transform: none;
        }

        /* ========================================================
           MODAL
           ======================================================== */

        .modal-charte .modal-content {
            border: none;
            border-radius: 20px;
            overflow: hidden;
            box-shadow: 0 30px 80px rgba(0,0,0,0.15);
        }

        .modal-header-charte {
            padding: 1.25rem 1.5rem;
            border: none;
        }

        .modal-header-charte .btn-close {
            filter: brightness(0) invert(1);
            opacity: 0.8;
        }

        .modal-header-icon {
            width: 48px;
            height: 48px;
            border-radius: 12px;
            background: rgba(255,255,255,0.2);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.5rem;
            color: white;
        }

        .summary-card-charte {
            background: linear-gradient(
                135deg,
                #f8fafc,
                #ffffff
            );
            padding: 1rem 1.25rem;
            border-radius: 12px;
            border: 1px solid #e2e8f0;
        }

        .supplier-avatar-modal {
            width: 52px;
            height: 52px;
            border-radius: 12px;
            background: linear-gradient(
                135deg,
                var(--primary),
                var(--primary-dark)
            );
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 1.3rem;
            flex-shrink: 0;
            box-shadow: 0 4px 12px rgba(41,90,167,0.25);
        }

        .alert-error-charte {
            border: none;
            background: #fef2f2;
            border-left: 4px solid var(--danger);
            border-radius: 16px;
            padding: 1rem 1.5rem;
            box-shadow: 0 8px 32px rgba(239,68,68,0.08);
        }

        .alert-error-icon {
            width: 48px;
            height: 48px;
            border-radius: 12px;
            background: rgba(239,68,68,0.1);
            display: flex;
            align-items: center;
            justify-content: center;
            color: var(--danger);
            font-size: 1.5rem;
            flex-shrink: 0;
        }

        /* ========================================================
           LOADING
           ======================================================== */

        .loading-page {
            min-height: calc(100vh - 140px);
        }

        .loading-spinner-charte {
            position: relative;
            width: 80px;
            height: 80px;
            margin: 0 auto;
        }

        .loading-ring-charte {
            position: absolute;
            inset: 0;
            border-radius: 50%;
            border: 4px solid #e2e8f0;
            border-top-color: var(--primary);
            animation: spin 1s linear infinite;
        }

        .loading-icon-charte {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            font-size: 28px;
            color: var(--primary);
        }

        /* ========================================================
           ERROR
           ======================================================== */

        .error-state-charte {
            background: white;
            border-radius: 12px;
            padding: 4rem 2rem;
            text-align: center;
            box-shadow: 0 2px 8px rgba(0,0,0,0.04);
            max-width: 650px;
            margin: 3rem auto;
        }

        .error-state-icon {
            width: 72px;
            height: 72px;
            border-radius: 50%;
            background: ${COLORS.danger || "#ef4444"}15;
            color: var(--danger);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 2rem;
            margin: 0 auto 1.25rem;
        }

        /* ========================================================
           TOAST
           ======================================================== */

        .toast-notification-fixed {
            position: fixed;
            bottom: 24px;
            right: 24px;
            z-index: 9999;
            animation: slideInRight 0.3s ease-out;
        }

        .toast-modern {
            background: white;
            border-radius: 16px;
            box-shadow: 0 20px 40px rgba(0,0,0,0.15);
            min-width: 320px;
            max-width: 400px;
            overflow: hidden;
        }

        .toast-header-custom {
            padding: 12px 20px;
            display: flex;
            align-items: center;
            justify-content: space-between;
        }

        .toast-close-btn {
            background: transparent;
            border: none;
            color: white;
            cursor: pointer;
            opacity: 0.8;
        }

        .toast-close-btn:hover {
            opacity: 1;
        }

        .toast-body-custom {
            padding: 16px 20px;
        }

        .toast-body-custom p {
            color: #1e293b;
            font-size: 0.9rem;
        }

        .toast-progress {
            height: 3px;
            width: 100%;
        }

        /* ========================================================
           ANIMATIONS
           ======================================================== */

        @keyframes spin {
            to {
                transform: rotate(360deg);
            }
        }

        @keyframes slideInRight {
            from {
                opacity: 0;
                transform: translateX(100%);
            }

            to {
                opacity: 1;
                transform: translateX(0);
            }
        }

        @keyframes shrink {
            from {
                width: 100%;
            }

            to {
                width: 0%;
            }
        }

        /* ========================================================
           RESPONSIVE
           ======================================================== */

        @media (max-width: 991px) {
            .profile-actions {
                justify-content: flex-start;
            }
        }

        @media (max-width: 768px) {
            .header-modern {
                padding: 1rem;
            }

            .header-icon-wrapper {
                width: 44px;
                height: 44px;
                font-size: 1.2rem;
            }

            .header-title {
                font-size: 1.1rem;
            }

            .supplier-avatar-charte {
                width: 62px;
                height: 62px;
                font-size: 1.6rem;
            }

            .supplier-name {
                font-size: 1.1rem;
            }

            .supplier-meta {
                font-size: 0.72rem;
            }

            .legal-grid {
                grid-template-columns: 1fr;
            }

            .legal-item-charte:nth-last-child(-n+2) {
                border-bottom: 1px solid #f1f5f9;
            }

            .legal-item-charte:last-child {
                border-bottom: none;
            }

            .toast-notification-fixed {
                bottom: 12px;
                right: 12px;
                left: 12px;
            }

            .toast-modern {
                min-width: auto;
                max-width: none;
            }

            .footer-info-card {
                padding: 1rem;
            }
        }

        @media (max-width: 576px) {
            .kpi-charte {
                padding: 1rem;
            }

            .kpi-value-charte {
                font-size: 1.15rem;
            }

            .info-item-charte {
                padding-left: 1rem;
                padding-right: 1rem;
            }

            .condition-box-charte {
                margin-left: 1rem;
                margin-right: 1rem;
            }

            .card-header-gradient {
                padding: 0.85rem 1rem;
            }

            .btn-modern-outline,
            .btn-modern-primary {
                padding: 0.45rem 1rem;
                font-size: 0.78rem;
            }
        }

        .rounded-3 {
            border-radius: 12px !important;
        }
    `;
}

